import { useCallback } from 'react';
import type { PermissionAction, PermissionSubject, UserType } from '@bliks-io/contracts';
import { useProfileQuery } from '@/api/queries/useProfile';

const EMPTY_USER_TYPES: UserType[] = [];

/**
 * Hook to access CASL-derived permissions from the profile response.
 * Primary hook for permission checks across the frontend.
 *
 * @example
 * ```tsx
 * const { can, permissions, isLoading } = usePermissions();
 * if (can('Process', 'create')) { ... }
 * ```
 */
export function usePermissions() {
  const { data, isLoading } = useProfileQuery();
  const permissions = data?.permissions;
  const authContext = data?.authContext;
  const userTypes: UserType[] = data?.data?.userTypes ?? EMPTY_USER_TYPES;

  const can = useCallback(
    (subject: PermissionSubject, action: PermissionAction): boolean => {
      if (!permissions) return false;
      return permissions[subject]?.[action] ?? false;
    },
    [permissions]
  );

  return {
    permissions,
    authContext,
    userTypes,
    isLoading,
    can
  };
}

/**
 * Org-Manager role check. The editor's pickers gate direct entity creation on
 * the ROLE, not on the entity-create permission: a department manager holds
 * `OrgFunction.create` but must still raise suggestions (ADR 0031).
 */
export function useIsOrgManager(): boolean {
  return usePermissions().userTypes.includes('org_manager');
}
